import React, { useState } from 'react';
import {
    Layout,
    Table,
    Button,
    Space,
    Input,
    Switch,
    Form,
    message,
    Typography,
    Popconfirm,
    Select,
    ConfigProvider,
    Radio,
    Tag,
    Row,
    Col,
    Drawer,
    InputNumber,
    Alert,
    Descriptions,
    Tooltip,
    Empty
} from 'antd';
import {
    PlusOutlined,
    SearchOutlined,
    ReloadOutlined,
    SettingOutlined,
    UserOutlined,
    GlobalOutlined,
    InfoCircleOutlined,
    DownOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    CloseOutlined
} from '@ant-design/icons';
import zhCN from 'antd/lib/locale/zh_CN';

const { Content } = Layout;
const { Text, Title } = Typography;
const { Option } = Select;

// ============================================================
// Mock 数据 — TODO: 联调时替换为接口数据
// ============================================================

// 服务类型（模拟 GET /package/service/listAll），aiiotTypeConfig 为算法配置 JSON 数组
const MOCK_SERVICES = [
    { id: 1, serviceName: '全天云存', serviceId: '300010', aiiotTypeConfig: '[{"AlgorithmType":1010,"AIIoTName":"云端录像","Config":{}},{"AlgorithmType":1013,"AIIoTName":"事件记录","Config":{}},{"AlgorithmType":1011,"AIIoTName":"云端抓图","Config":{}}]' },
    { id: 2, serviceName: '事件云存', serviceId: '300011', aiiotTypeConfig: '[{"AlgorithmType":1010,"AIIoTName":"云端录像","Config":{}},{"AlgorithmType":1013,"AIIoTName":"事件记录","Config":{}},{"AlgorithmType":1011,"AIIoTName":"云端抓图","Config":{}}]' },
    { id: 3, serviceName: '逗留提醒', serviceId: '300036', aiiotTypeConfig: '[{"AlgorithmType":1011,"AIIoTName":"云端抓图","Config":{}}]' },
    { id: 4, serviceName: '人脸识别', serviceId: '300033', aiiotTypeConfig: '[{"AlgorithmType":1011,"AIIoTName":"云端抓图","Config":{}},{"AlgorithmType":1013,"AIIoTName":"事件记录","Config":{}}]' },
    { id: 5, serviceName: '短信服务', serviceId: '300031', aiiotTypeConfig: '[]' },
    { id: 6, serviceName: 'AI家庭看护', serviceId: '400001', aiiotTypeConfig: '[{"AlgorithmType":4003,"AIIoTName":"AI智能体","Config":{}}]' },
];

// Agent 列表（模拟 getPublishedAgentList，成本字段由列表接口返回）
const MOCK_AGENTS = [
    {
        agentId: 'test-agent-001', name: '家庭安防智能看护',
        description: '7×24小时家庭安全守护，自动识别异常事件并生成AI报告',
        captureMode: '混合模式（事件触发+定时）', eventEnabled: true, searchDays: 7, summaryEnabled: true,
        captureNum: 200, regionNames: '国内',
        estimatedInputTokens: 1.0, estimatedOutputTokens: 3.0, inputPrice: 0.003, outputPrice: 0.012, totalCost: 0.0048,
    },
    {
        agentId: 'agent-pet-002', name: '宠物监护Agent',
        description: '宠物行为分析与健康监护，异常活动检测',
        captureMode: '事件触发', eventEnabled: true, searchDays: 30, summaryEnabled: true,
        captureNum: 288, regionNames: '国内',
        estimatedInputTokens: 0.8, estimatedOutputTokens: 2.5, inputPrice: 0.001, outputPrice: 0.004, totalCost: 0.0108,
    },
];

// 套餐服务（模板）列表（模拟 GET /package/template/list），价格单位为「分」
const INIT_TEMPLATES = [
    { id: '1001', name: '全能家居半年包', serviceId: 6, serviceName: 'AI家庭看护', basePriceCny: 19900, basePriceUsd: 2999, monthBasePriceCny: 3317, monthBasePriceUsd: 500, serviceDuration: 6, serviceDurationUnit: 2, status: 1, sdkVersionLimit: 1, sdkVersionStart: 0, sdkVersionEnd: 0, serviceConfig: '[]', updatedAt: 1740758400 },
    { id: '1002', name: '全能家居年包', serviceId: 1, serviceName: '全天云存', basePriceCny: 36500, basePriceUsd: 5999, monthBasePriceCny: 3042, monthBasePriceUsd: 500, serviceDuration: 12, serviceDurationUnit: 2, status: 1, sdkVersionLimit: 2, sdkVersionStart: 1, sdkVersionEnd: 9, serviceConfig: '[]', updatedAt: 1741104000 },
    { id: '3001', name: 'AI分析尊享季包', serviceId: 4, serviceName: '人脸识别', basePriceCny: 8800, basePriceUsd: 1350, monthBasePriceCny: 2933, monthBasePriceUsd: 450, serviceDuration: 3, serviceDurationUnit: 2, status: 2, sdkVersionLimit: 1, sdkVersionStart: 0, sdkVersionEnd: 0, serviceConfig: '[]', updatedAt: 1741536000 },
];

// 各算法类型的默认配置（作为可配置项的兜底默认值）
const DEFAULT_CONFIGS = {
    1010: { StorageAbility: 0, StorageList: [{ LoopDays: '7', RecordType: '1' }], StreamID: 0, Duration: 30 },
    1011: { GifFlag: 0, GifOpenFlag: 0, PicType: 1, LoopDays: 7, RecordType: 1, SnapJpgCnt: 1, StorageType: 0, Interval: 30, OpenFlag: 1 },
    1013: { LoopDays: 7 },
    4003: {},
};

// 生效时长单位：1=天 2=月 3=年
const DURATION_UNIT_TEXT = { 1: '天', 2: '个月', 3: '年' };

// 分转元（展示）
const fenToYuan = (fen) => (Number(fen || 0) / 100).toFixed(2);
// 元转分（提交，避免浮点精度问题）
const yuanToFen = (yuan) => Math.round(Number(yuan) * 100);

// 分区标题
const SectionTitle = ({ children }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={{ width: 4, height: 18, background: '#1890ff', borderRadius: 2 }} />
        <Text strong style={{ fontSize: 16 }}>{children}</Text>
    </div>
);

const PackageCombination = () => {
    // ============================================================
    // 状态
    // ============================================================
    const [templatesData, setTemplatesData] = useState(INIT_TEMPLATES);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [form] = Form.useForm();
    const [searchForm] = Form.useForm();
    const [searchParams, setSearchParams] = useState({ searchType: 'name' });

    // 动态算法配置（key = AlgorithmType）
    const [algorithmConfigs, setAlgorithmConfigs] = useState({});
    const [collapsedMap, setCollapsedMap] = useState({});

    // 监听表单字段
    const serviceId = Form.useWatch('serviceId', form);
    const sdkVersionLimit = Form.useWatch('sdkVersionLimit', form) ?? 1;

    // ============================================================
    // 服务类型 → 算法列表推导
    // ============================================================
    const parseAlgorithms = (sid) => {
        if (!sid) return [];
        const st = MOCK_SERVICES.find(s => s.id === sid);
        if (!st) return [];
        try { return JSON.parse(st.aiiotTypeConfig || '[]'); } catch { return []; }
    };

    const currentServiceAlgorithms = parseAlgorithms(serviceId);
    const agentAlgorithms = currentServiceAlgorithms.filter(a => Number(a.AlgorithmType) === 4003);

    const iotAlgorithms = (() => {
        const list = currentServiceAlgorithms.filter(a => Number(a.AlgorithmType) !== 4003);
        if (agentAlgorithms.length) {
            if (!list.find(a => Number(a.AlgorithmType) === 1011)) list.push({ AlgorithmType: 1011, AIIoTName: '云端抓图', Config: {} });
            if (!list.find(a => Number(a.AlgorithmType) === 1013)) list.push({ AlgorithmType: 1013, AIIoTName: '事件记录', Config: {} });
        }
        return list;
    })();

    const allDisplayAlgorithms = [...agentAlgorithms, ...iotAlgorithms];
    const currentServiceInfo = serviceId ? MOCK_SERVICES.find(s => s.id === serviceId) : null;
    const selectedAgent = MOCK_AGENTS.find(a => a.agentId === (algorithmConfigs[4003] || {}).AgentID);

    // ============================================================
    // 算法配置初始化
    // ============================================================
    const initAlgorithmConfigs = (sid, savedConfigs) => {
        if (!sid) { setAlgorithmConfigs({}); setCollapsedMap({}); return; }
        const st = MOCK_SERVICES.find(s => s.id === sid);
        let algos = [];
        if (st) { try { algos = JSON.parse(st.aiiotTypeConfig || '[]'); } catch { algos = []; } }

        const agent = algos.filter(a => Number(a.AlgorithmType) === 4003);
        const iot = algos.filter(a => Number(a.AlgorithmType) !== 4003);
        if (agent.length) {
            if (!iot.find(a => Number(a.AlgorithmType) === 1011)) iot.push({ AlgorithmType: 1011, AIIoTName: '云端抓图', Config: {} });
            if (!iot.find(a => Number(a.AlgorithmType) === 1013)) iot.push({ AlgorithmType: 1013, AIIoTName: '事件记录', Config: {} });
        }

        const all = [...agent, ...iot];
        const configs = {};
        const collapsed = {};
        all.forEach(a => {
            const type = a.AlgorithmType;
            const saved = (savedConfigs || []).find(c => Number(c.AlgorithmType) === Number(type));
            configs[type] = { ...(DEFAULT_CONFIGS[type] || {}), ...(saved?.Config || {}) };
            collapsed[type] = !(type in DEFAULT_CONFIGS);
        });
        setAlgorithmConfigs(configs);
        setCollapsedMap(collapsed);
    };

    const setAlgoConfig = (type, patch) => {
        setAlgorithmConfigs(prev => ({ ...prev, [type]: { ...prev[type], ...patch } }));
    };

    const handleServiceTypeChange = (sid) => {
        initAlgorithmConfigs(sid, null);
    };

    // ============================================================
    // 搜索
    // ============================================================
    const getFilteredData = () => {
        return templatesData.filter(item => {
            const { searchType, keyword, serviceTypeId } = searchParams;
            if (keyword) {
                if (searchType === 'name' && !(item.name || '').toLowerCase().includes(keyword.toLowerCase())) return false;
                if (searchType === 'id' && !String(item.id).toLowerCase().includes(keyword.toLowerCase())) return false;
            }
            if (serviceTypeId && Number(item.serviceId) !== Number(serviceTypeId)) return false;
            return true;
        });
    };

    const handleSearch = () => setSearchParams(searchForm.getFieldsValue());

    const handleReset = () => {
        searchForm.resetFields();
        searchForm.setFieldsValue({ searchType: 'name' });
        setSearchParams({ searchType: 'name' });
    };

    // ============================================================
    // 新增 / 编辑
    // ============================================================
    const openCreate = () => {
        setEditingTemplate(null);
        form.resetFields();
        setAlgorithmConfigs({});
        setCollapsedMap({});
        setDrawerVisible(true);
    };

    const openEdit = (record) => {
        setEditingTemplate(record);
        form.setFieldsValue({
            serviceId: record.serviceId,
            name: record.name,
            serviceDuration: record.serviceDuration,
            serviceDurationUnit: record.serviceDurationUnit ?? 2,
            applicationDirection: record.applicationDirection ?? 2,
            basePriceCny: fenToYuan(record.basePriceCny),
            basePriceUsd: fenToYuan(record.basePriceUsd),
            monthBasePriceCny: fenToYuan(record.monthBasePriceCny),
            monthBasePriceUsd: fenToYuan(record.monthBasePriceUsd),
            sdkVersionLimit: record.sdkVersionLimit ?? 1,
            sdkVersionStart: record.sdkVersionStart,
            sdkVersionEnd: record.sdkVersionEnd,
            desc: record.desc,
        });
        let savedConfigs = [];
        try { savedConfigs = JSON.parse(record.serviceConfig || '[]'); } catch { savedConfigs = []; }
        initAlgorithmConfigs(record.serviceId, savedConfigs);
        setDrawerVisible(true);
    };

    const closeDrawer = () => {
        setDrawerVisible(false);
        setEditingTemplate(null);
        form.resetFields();
        setAlgorithmConfigs({});
        setCollapsedMap({});
    };

    const handleSubmit = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch { return; }

        const serviceConfig = allDisplayAlgorithms.map(algo => ({
            AlgorithmType: algo.AlgorithmType,
            AIIoTName: algo.AIIoTName,
            Config: algorithmConfigs[algo.AlgorithmType] || {},
        }));

        const now = Math.floor(Date.now() / 1000);
        const serviceName = MOCK_SERVICES.find(s => s.id === values.serviceId)?.serviceName || '';
        const payload = {
            name: values.name,
            serviceId: values.serviceId,
            serviceName,
            serviceConfig: JSON.stringify(serviceConfig),
            serviceDuration: Number(values.serviceDuration),
            serviceDurationUnit: values.serviceDurationUnit,
            applicationDirection: values.applicationDirection,
            basePriceCny: yuanToFen(values.basePriceCny),
            basePriceUsd: yuanToFen(values.basePriceUsd),
            monthBasePriceCny: yuanToFen(values.monthBasePriceCny),
            monthBasePriceUsd: yuanToFen(values.monthBasePriceUsd),
            sdkVersionLimit: values.sdkVersionLimit,
            sdkVersionStart: values.sdkVersionLimit === 2 ? parseInt(values.sdkVersionStart) || 0 : 0,
            sdkVersionEnd: values.sdkVersionLimit === 2 ? parseInt(values.sdkVersionEnd) || 0 : 0,
            desc: values.desc,
        };

        if (editingTemplate) {
            setTemplatesData(prev => prev.map(t => t.id === editingTemplate.id ? { ...t, ...payload, updatedAt: now } : t));
            message.success('修改成功');
        } else {
            setTemplatesData(prev => [{ id: `${Date.now()}`, ...payload, status: 1, updatedAt: now }, ...prev]);
            message.success('创建成功');
        }
        closeDrawer();
    };

    // ============================================================
    // 开关 / 删除
    // ============================================================
    const handleToggleStatus = (record, checked) => {
        setTemplatesData(prev => prev.map(t => t.id === record.id ? { ...t, status: checked ? 1 : 2 } : t));
        message.success(checked ? '已启用' : '已停用');
    };

    const handleDelete = (record) => {
        setTemplatesData(prev => prev.filter(t => t.id !== record.id));
        message.success('删除成功');
    };

    // ============================================================
    // 列表列
    // ============================================================
    const templateColumns = [
        {
            title: '套餐服务信息', dataIndex: 'name', key: 'name', fixed: 'left', width: 170,
            render: (text, record) => (
                <div>
                    <div style={{ fontWeight: 500, color: 'rgba(0,0,0,0.85)', marginBottom: 4 }}>{text}</div>
                    <div style={{ color: '#8c8c8c', fontSize: 12 }}>ID:{record.id}</div>
                </div>
            ),
        },
        {
            title: '服务底价', key: 'basePrice', width: 160,
            render: (_, r) => (
                <div style={{ fontSize: 12, lineHeight: '1.5' }}>
                    <div>国内：<Text strong>¥{fenToYuan(r.basePriceCny)}</Text></div>
                    <div>海外：<Text strong>${fenToYuan(r.basePriceUsd)}</Text></div>
                </div>
            ),
        },
        {
            title: '单月底价', key: 'monthBasePrice', width: 160,
            render: (_, r) => (
                <div style={{ fontSize: 12, lineHeight: '1.5' }}>
                    <div>国内：<Text type="secondary">¥{fenToYuan(r.monthBasePriceCny)}</Text></div>
                    <div>海外：<Text type="secondary">${fenToYuan(r.monthBasePriceUsd)}</Text></div>
                </div>
            ),
        },
        { title: '服务类型', dataIndex: 'serviceName', key: 'serviceName', width: 140 },
        {
            title: '生效时长', key: 'duration', width: 120,
            render: (_, r) => `${r.serviceDuration}${DURATION_UNIT_TEXT[r.serviceDurationUnit] || ''}`,
        },
        {
            title: '对企业开放', dataIndex: 'status', key: 'status', width: 110, align: 'center',
            render: (status, record) => (
                <Switch
                    size="small"
                    checked={status === 1}
                    checkedChildren="开"
                    unCheckedChildren="关"
                    onChange={(checked) => handleToggleStatus(record, checked)}
                />
            ),
        },
        {
            title: 'SDK版本', key: 'sdk', width: 150,
            render: (_, r) => r.sdkVersionLimit === 2 ? `${r.sdkVersionStart || '0'} ~ ${r.sdkVersionEnd || '无限制'}` : '无限制',
        },
        {
            title: '最后更新时间', dataIndex: 'updatedAt', key: 'updatedAt', width: 170,
            render: (t) => t ? new Date(t * 1000).toLocaleString('zh-CN') : '-',
        },
        {
            title: '操作', key: 'action', fixed: 'right', width: 130, align: 'center',
            render: (_, record) => (
                <Space size={0}>
                    <Button type="link" size="small" onClick={() => openEdit(record)}>编辑</Button>
                    <Popconfirm
                        title={`确定删除套餐服务「${record.name}」吗？`}
                        onConfirm={() => handleDelete(record)}
                        okText="确定"
                        cancelText="取消"
                    >
                        <Button type="link" size="small" danger>删除</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    // ============================================================
    // 算法配置渲染（按 AlgorithmType 分发）
    // ============================================================
    const renderAlgoConfig = (type) => {
        const cfg = algorithmConfigs[type] || {};

        // 1010 云端录像
        if (type === 1010) {
            const list = cfg.StorageList || [];
            const moveUp = (i) => { if (i === 0) return; const l = [...list]; [l[i - 1], l[i]] = [l[i], l[i - 1]]; setAlgoConfig(1010, { StorageList: l }); };
            const moveDown = (i) => { if (i === list.length - 1) return; const l = [...list]; [l[i + 1], l[i]] = [l[i], l[i + 1]]; setAlgoConfig(1010, { StorageList: l }); };
            const remove = (i) => setAlgoConfig(1010, { StorageList: list.filter((_, idx) => idx !== i) });
            const add = () => setAlgoConfig(1010, { StorageList: [...list, { LoopDays: '0', RecordType: '1' }] });
            const setItem = (i, key, val) => { const l = [...list]; l[i] = { ...l[i], [key]: val }; setAlgoConfig(1010, { StorageList: l }); };

            return (
                <div>
                    <Form.Item label="默认报警录制最大时长(秒)">
                        <InputNumber min={0} value={cfg.Duration} onChange={v => setAlgoConfig(1010, { Duration: v })} style={{ width: 200 }} />
                    </Form.Item>
                    <Form.Item label="录制码流">
                        <Select value={cfg.StreamID} onChange={v => setAlgoConfig(1010, { StreamID: v })} style={{ width: 180 }}>
                            <Option value={0}>主码流(码流ID: 0)</Option>
                            <Option value={1}>子码流(码流ID: 1)</Option>
                            <Option value={2}>次码流(码流ID: 2)</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item label="存储列表">
                        <div style={{ width: 480, border: '1px solid #e4e7ed', borderRadius: 6, overflow: 'hidden' }}>
                            <div style={{ display: 'flex', alignItems: 'center', padding: '0 12px', gap: 8, background: '#f5f7fa', height: 38, fontSize: 13, color: '#606266', fontWeight: 500, borderBottom: '1px solid #e4e7ed' }}>
                                <span style={{ width: 32, textAlign: 'center', color: '#909399' }}>#</span>
                                <span style={{ flex: 1 }}>存储天数</span>
                                <span style={{ flex: 1 }}>存储方式</span>
                                <span style={{ width: 72, textAlign: 'right' }}>操作</span>
                            </div>
                            {list.map((item, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '0 12px', gap: 8, height: 52, borderBottom: '1px solid #f0f0f0' }}>
                                    <span style={{ width: 32, textAlign: 'center', color: '#909399', fontSize: 13 }}>{i + 1}</span>
                                    <span style={{ flex: 1 }}><InputNumber min={0} value={Number(item.LoopDays)} onChange={v => setItem(i, 'LoopDays', String(v))} style={{ width: '100%' }} /></span>
                                    <span style={{ flex: 1 }}>
                                        <Select value={item.RecordType} onChange={v => setItem(i, 'RecordType', v)} style={{ width: '100%' }}>
                                            <Option value="1">全天</Option>
                                            <Option value="2">事件</Option>
                                        </Select>
                                    </span>
                                    <span style={{ width: 72, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                                        <ArrowUpOutlined style={{ color: '#c0c4cc', cursor: 'pointer' }} onClick={() => moveUp(i)} />
                                        <ArrowDownOutlined style={{ color: '#c0c4cc', cursor: 'pointer' }} onClick={() => moveDown(i)} />
                                        <CloseOutlined style={{ color: '#c0c4cc', cursor: 'pointer' }} onClick={() => remove(i)} />
                                    </span>
                                </div>
                            ))}
                            <div onClick={add} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, height: 40, cursor: 'pointer', color: '#909399', fontSize: 13, background: '#fafafa', borderTop: '1px dashed #e4e7ed' }}>
                                <PlusOutlined /> 添加
                            </div>
                        </div>
                    </Form.Item>
                    <Form.Item label="云存能力">
                        <Select disabled value={cfg.StorageAbility} style={{ width: 180 }}>
                            <Option value={0}>不支持</Option>
                            <Option value={1}>全天</Option>
                            <Option value={2}>事件</Option>
                        </Select>
                    </Form.Item>
                </div>
            );
        }

        // 1011 云端抓图
        if (type === 1011) {
            return (
                <div>
                    <Form.Item label="抓图质量">
                        <Select value={cfg.PicType} onChange={v => setAlgoConfig(1011, { PicType: v })} style={{ width: 200 }}>
                            <Option value={1}>大图</Option>
                            <Option value={2}>小图</Option>
                            <Option value={4}>320*240</Option>
                            <Option value={8}>64*48</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item label="保存天数">
                        <InputNumber min={1} value={cfg.LoopDays} onChange={v => setAlgoConfig(1011, { LoopDays: v })} style={{ width: 200 }} /> <span style={{ color: '#8c8c8c', marginLeft: 8 }}>天</span>
                    </Form.Item>
                    <Form.Item label="默认开启">
                        <Radio.Group value={cfg.OpenFlag} onChange={e => setAlgoConfig(1011, { OpenFlag: e.target.value })}>
                            <Radio value={1}>开启</Radio>
                            <Radio value={0}>关闭</Radio>
                        </Radio.Group>
                    </Form.Item>
                    <Form.Item label="支持动图">
                        <Radio.Group value={cfg.GifFlag} onChange={e => setAlgoConfig(1011, { GifFlag: e.target.value })}>
                            <Radio value={1}>是</Radio>
                            <Radio value={0}>否</Radio>
                        </Radio.Group>
                    </Form.Item>
                    {cfg.GifFlag === 1 && (
                        <Form.Item label="动图开关">
                            <Radio.Group value={cfg.GifOpenFlag} onChange={e => setAlgoConfig(1011, { GifOpenFlag: e.target.value })}>
                                <Radio value={1}>开启</Radio>
                                <Radio value={0}>关闭</Radio>
                            </Radio.Group>
                        </Form.Item>
                    )}
                    <Form.Item label="抓图数量">
                        <InputNumber min={1} max={100} value={cfg.SnapJpgCnt} onChange={v => setAlgoConfig(1011, { SnapJpgCnt: v })} style={{ width: 200 }} />
                    </Form.Item>
                    <Form.Item label="抓拍间隔">
                        <Select value={cfg.Interval} onChange={v => setAlgoConfig(1011, { Interval: v })} style={{ width: 200 }}>
                            <Option value={30}>30秒</Option>
                            <Option value={60}>60秒</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item label="存储方式">
                        <Radio.Group value={cfg.RecordType} onChange={e => setAlgoConfig(1011, { RecordType: e.target.value })}>
                            <Radio value={1}>事件</Radio>
                            <Radio value={2}>定时</Radio>
                        </Radio.Group>
                    </Form.Item>
                    <Form.Item label="存储类型">
                        <Radio.Group value={cfg.StorageType} onChange={e => setAlgoConfig(1011, { StorageType: e.target.value })}>
                            <Radio value={0}>私有格式</Radio>
                            <Radio value={1}>标准jpg</Radio>
                        </Radio.Group>
                    </Form.Item>
                </div>
            );
        }

        // 1013 事件记录
        if (type === 1013) {
            return (
                <div>
                    <Form.Item label="存储天数">
                        <InputNumber min={1} value={cfg.LoopDays} onChange={v => setAlgoConfig(1013, { LoopDays: v })} style={{ width: 200 }} /> <span style={{ color: '#8c8c8c', marginLeft: 8 }}>天</span>
                    </Form.Item>
                </div>
            );
        }

        return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无配置项" />;
    };

    return (
        <ConfigProvider locale={zhCN}>
            <Layout style={{ height: 'calc(100vh - 120px)', background: '#fff' }}>
                <Content style={{ padding: 0, display: 'flex', flexDirection: 'column', background: '#fff' }}>
                    {/* 标题栏 */}
                    <div style={{ padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Space direction="vertical" size={2}>
                            <Title level={4} style={{ margin: 0 }}>套餐服务管理</Title>
                        </Space>
                        <Space>
                            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>新增套餐服务</Button>
                        </Space>
                    </div>

                    {/* 搜索栏 */}
                    <div style={{ padding: '16px 24px 0 24px', background: '#fff' }}>
                        <Form form={searchForm} layout="inline" initialValues={{ searchType: 'name' }} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px 0' }}>
                            <Form.Item style={{ marginRight: 10, marginBottom: 0 }}>
                                <Input.Group compact>
                                    <Form.Item name="searchType" noStyle>
                                        <Select style={{ width: 120 }}>
                                            <Option value="name">套餐服务</Option>
                                            <Option value="id">ID</Option>
                                        </Select>
                                    </Form.Item>
                                    <Form.Item name="keyword" noStyle>
                                        <Input placeholder="输入关键字搜索" allowClear style={{ width: 220 }} />
                                    </Form.Item>
                                </Input.Group>
                            </Form.Item>
                            <Form.Item name="serviceTypeId" style={{ marginBottom: 0 }}>
                                <Select placeholder="请选择服务类型" allowClear style={{ width: 180 }}>
                                    {MOCK_SERVICES.map(s => <Option key={s.id} value={s.id}>{s.serviceName}</Option>)}
                                </Select>
                            </Form.Item>
                            <Form.Item style={{ marginBottom: 0 }}>
                                <Space>
                                    <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>查询</Button>
                                    <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
                                </Space>
                            </Form.Item>
                        </Form>
                    </div>

                    {/* 列表 */}
                    <div style={{ flex: 1, padding: '16px 24px', overflow: 'auto' }}>
                        <Table
                            columns={templateColumns}
                            dataSource={getFilteredData()}
                            rowKey="id"
                            scroll={{ x: 1200 }}
                            size="middle"
                            pagination={{
                                showTotal: (total) => `共 ${total} 条数据`,
                                defaultPageSize: 10,
                                showSizeChanger: true,
                            }}
                        />
                    </div>
                </Content>

                {/* 新增/编辑抽屉 */}
                <Drawer
                    title={editingTemplate ? '编辑套餐服务' : '新增套餐服务'}
                    width={900}
                    open={drawerVisible}
                    onClose={closeDrawer}
                    forceRender
                    bodyStyle={{ padding: 0, background: '#fff' }}
                    footer={
                        <div style={{ textAlign: 'right', padding: '10px 16px', background: '#fff', borderTop: '1px solid #f0f0f0' }}>
                            <Button onClick={closeDrawer} style={{ marginRight: 8 }}>取消</Button>
                            <Button type="primary" onClick={handleSubmit}>保存</Button>
                        </div>
                    }
                >
                    <div style={{ padding: '24px 32px' }}>
                        <Form form={form} layout="vertical">
                            {/* 选择服务类型 */}
                            <SectionTitle>选择服务类型</SectionTitle>
                            <Form.Item label="服务类型" name="serviceId" rules={[{ required: true, message: '请选择服务类型' }]}>
                                <Select placeholder="请选择服务类型" style={{ width: '100%' }} onChange={handleServiceTypeChange}>
                                    {MOCK_SERVICES.map(st => <Option key={st.id} value={st.id}>{st.serviceName}</Option>)}
                                </Select>
                            </Form.Item>

                            {/* Agent 专属配置 */}
                            {agentAlgorithms.length > 0 && (
                                <div style={{ background: '#f0f9eb', border: '1px solid #e1f3d8', borderRadius: 8, padding: '16px 20px', marginTop: 16 }}>
                                    <SectionTitle>Agent专属配置</SectionTitle>
                                    <Alert type="info" showIcon message="以下配置由所选服务类型自动关联，仅支持修改参数值。" style={{ marginBottom: 16 }} />
                                    <Form.Item
                                        label={(
                                            <span>
                                                选择Agent
                                                <Tooltip title="选择关联的Agent实例，套餐开通后将自动激活该Agent的AI事件识别能力">
                                                    <InfoCircleOutlined style={{ color: '#909399', marginLeft: 4 }} />
                                                </Tooltip>
                                            </span>
                                        )}
                                        required
                                    >
                                        <Select
                                            value={(algorithmConfigs[4003] || {}).AgentID}
                                            onChange={v => setAlgoConfig(4003, { AgentID: v })}
                                            placeholder="请选择要关联的Agent"
                                            allowClear
                                            showSearch
                                            optionFilterProp="children"
                                            style={{ width: '100%' }}
                                        >
                                            {MOCK_AGENTS.map(a => <Option key={a.agentId} value={a.agentId}>{a.name}（{a.agentId}）</Option>)}
                                        </Select>
                                    </Form.Item>

                                    {selectedAgent && (
                                        <div style={{ background: '#fff', border: '1px solid #e1f3d8', borderRadius: 6, padding: '12px 16px', marginTop: 12 }}>
                                            <Descriptions column={2} size="small" colon={false} labelStyle={{ color: '#909399', fontSize: 13 }}>
                                                <Descriptions.Item label="Agent名称"><Text strong>{selectedAgent.name}</Text></Descriptions.Item>
                                                <Descriptions.Item label="描述">{selectedAgent.description}</Descriptions.Item>
                                                <Descriptions.Item label="抓图策略">{selectedAgent.captureMode}</Descriptions.Item>
                                                <Descriptions.Item label="事件识别">
                                                    <Tag color={selectedAgent.eventEnabled ? 'green' : 'default'} style={{ border: 'none' }}>{selectedAgent.eventEnabled ? '支持' : '不支持'}</Tag>
                                                </Descriptions.Item>
                                                <Descriptions.Item label="AI搜索">
                                                    <Tag color={selectedAgent.searchDays > 0 ? 'green' : 'default'} style={{ border: 'none' }}>{selectedAgent.searchDays > 0 ? '支持' : '不支持'}</Tag>
                                                </Descriptions.Item>
                                                <Descriptions.Item label="阶段总结">
                                                    <Tag color={selectedAgent.summaryEnabled ? 'green' : 'default'} style={{ border: 'none' }}>{selectedAgent.summaryEnabled ? '支持' : '不支持'}</Tag>
                                                </Descriptions.Item>
                                                <Descriptions.Item label="生效区域">{selectedAgent.regionNames}</Descriptions.Item>
                                                <Descriptions.Item label="每日抓图上限"><Text strong>{selectedAgent.captureNum} 张/天</Text></Descriptions.Item>
                                                <Descriptions.Item label="平台预估成本">
                                                    <div>
                                                        <div style={{ fontWeight: 600, fontSize: 14 }}>¥{selectedAgent.totalCost.toFixed(4)}</div>
                                                        {selectedAgent.estimatedInputTokens > 0 && (
                                                            <div style={{ fontSize: 12, color: '#909399' }}>{selectedAgent.estimatedInputTokens.toFixed(2)}百万×¥{selectedAgent.inputPrice.toFixed(4)}</div>
                                                        )}
                                                        {selectedAgent.estimatedOutputTokens > 0 && (
                                                            <div style={{ fontSize: 12, color: '#909399' }}>+ {selectedAgent.estimatedOutputTokens.toFixed(2)}百万×¥{selectedAgent.outputPrice.toFixed(4)}</div>
                                                        )}
                                                    </div>
                                                </Descriptions.Item>
                                            </Descriptions>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* 关联算法参数配置 */}
                            {iotAlgorithms.length > 0 && (
                                <div style={{ marginTop: 24 }}>
                                    <SectionTitle>关联算法参数配置</SectionTitle>
                                    <Alert type="info" showIcon message="以下算法由所选服务类型自动关联，仅支持修改参数值。" style={{ marginBottom: 16 }} />
                                    <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, overflow: 'hidden' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#faad14' }} />
                                            <Text strong>{currentServiceInfo?.serviceName || '算法配置'}</Text>
                                        </div>
                                        <div style={{ padding: 16 }}>
                                            {iotAlgorithms.map(algo => {
                                                const type = algo.AlgorithmType;
                                                const hasCfg = type in DEFAULT_CONFIGS;
                                                const collapsed = collapsedMap[type];
                                                return (
                                                    <div key={type} style={{ border: '1px solid #f0f0f0', borderRadius: 8, marginBottom: 12, overflow: 'hidden' }}>
                                                        <div
                                                            onClick={() => setCollapsedMap(prev => ({ ...prev, [type]: !prev[type] }))}
                                                            style={{ display: 'flex', alignItems: 'center', padding: '10px 16px', background: '#fafafa', borderBottom: collapsed ? 'none' : '1px solid #f0f0f0', cursor: 'pointer', userSelect: 'none' }}
                                                        >
                                                            <DownOutlined style={{ transform: collapsed ? 'rotate(-90deg)' : 'none', transition: 'transform 0.2s' }} />
                                                            <Tag color="blue" style={{ marginLeft: 8, borderRadius: 4, border: 'none' }}>{type}</Tag>
                                                            <span style={{ marginLeft: 8 }}>{algo.AIIoTName}</span>
                                                            {!hasCfg && <Tag style={{ marginLeft: 'auto', marginRight: 0 }}>无配置项</Tag>}
                                                        </div>
                                                        {!collapsed && (
                                                            <div style={{ padding: 16 }}>
                                                                {hasCfg ? renderAlgoConfig(type) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无配置项" />}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {agentAlgorithms.length === 0 && iotAlgorithms.length === 0 && (
                                <Alert type="warning" showIcon message="暂无配置项" style={{ marginTop: 16 }} />
                            )}

                            {/* 基础属性 */}
                            <div style={{ marginTop: 24 }}>
                                <SectionTitle>基础属性</SectionTitle>
                                <Form.Item label="套餐服务名称" name="name" rules={[{ required: true, message: '请输入套餐服务名称' }]}>
                                    <Input placeholder="输入套餐服务名称，如：高级报警套餐A" maxLength={20} showCount />
                                </Form.Item>
                                <Form.Item label="生效时长" required style={{ marginBottom: 0 }}>
                                    <Input.Group compact style={{ display: 'flex' }}>
                                        <Form.Item name="serviceDuration" noStyle rules={[{ required: true, message: '请输入生效时长' }]}>
                                            <InputNumber min={1} style={{ flex: 1 }} placeholder="时长" />
                                        </Form.Item>
                                        <Form.Item name="serviceDurationUnit" noStyle initialValue={2}>
                                            <Select style={{ width: 100 }}>
                                                <Option value={1}>天</Option>
                                                <Option value={2}>月</Option>
                                                <Option value={3}>年</Option>
                                            </Select>
                                        </Form.Item>
                                    </Input.Group>
                                </Form.Item>
                                <Form.Item label="应用方向" name="applicationDirection" initialValue={2}>
                                    <Radio.Group>
                                        <Radio.Button value={1}><SettingOutlined /> 设备</Radio.Button>
                                        <Radio.Button value={2}><UserOutlined /> 用户</Radio.Button>
                                    </Radio.Group>
                                </Form.Item>
                            </div>

                            {/* 商务底价设置 */}
                            <div style={{ marginTop: 24 }}>
                                <SectionTitle>商务底价设置</SectionTitle>
                                <Alert type="warning" showIcon message="App企业在使用此组合服务模板创建、售卖套餐，需向平台支付《服务底价》费用。" style={{ marginBottom: 16 }} />
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item label="服务底价 (国内)" name="basePriceCny" rules={[{ required: true, message: '请输入服务底价(国内)' }]}>
                                            <InputNumber precision={2} min={0} style={{ width: '100%' }} placeholder="0.00" prefix={<span style={{ color: '#8c8c8c' }}>¥</span>} addonAfter="元" />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item label="服务底价 (海外)" name="basePriceUsd" rules={[{ required: true, message: '请输入服务底价(海外)' }]}>
                                            <InputNumber precision={2} min={0} style={{ width: '100%' }} placeholder="0.00" prefix={<GlobalOutlined style={{ color: '#8c8c8c' }} />} addonAfter="USD" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item label="单月底价 (国内)" name="monthBasePriceCny" rules={[{ required: true, message: '请输入单月底价(国内)' }]}>
                                            <InputNumber precision={2} min={0} style={{ width: '100%' }} placeholder="0.00" prefix={<span style={{ color: '#8c8c8c' }}>¥</span>} addonAfter="元" />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item label="单月底价 (海外)" name="monthBasePriceUsd" rules={[{ required: true, message: '请输入单月底价(海外)' }]}>
                                            <InputNumber precision={2} min={0} style={{ width: '100%' }} placeholder="0.00" prefix={<GlobalOutlined style={{ color: '#8c8c8c' }} />} addonAfter="USD" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <div style={{ fontSize: 12, color: '#8c8c8c', lineHeight: '1.6', marginTop: -8, marginBottom: 16 }}>
                                    <div>1、当此模版套餐发生退款操作，平台需退还（部分）企业已支付的《服务底价》</div>
                                    <div>2、退底价公式：服务底价 - 单月底价 * 已生效月份（不足一个月，按照一个月计算）</div>
                                </div>
                            </div>

                            {/* 限购策略 */}
                            <div style={{ marginTop: 24 }}>
                                <SectionTitle>限购策略</SectionTitle>
                                <Form.Item label="SDK 版本要求" name="sdkVersionLimit" initialValue={1}>
                                    <Radio.Group>
                                        <Radio value={1}>不限版本</Radio>
                                        <Radio value={2}>限制版本</Radio>
                                    </Radio.Group>
                                </Form.Item>
                                {sdkVersionLimit === 2 && (
                                    <div style={{ background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                                        <Row gutter={16}>
                                            <Col span={11}>
                                                <Form.Item
                                                    name="sdkVersionStart"
                                                    rules={[{ required: true, message: '请输入起始版本' }, { pattern: /^[1-9]\d*$/, message: 'SDK版本必须为正整数' }]}
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <Input placeholder="请输入起始版本号(正整数)" />
                                                </Form.Item>
                                            </Col>
                                            <Col span={2} style={{ textAlign: 'center', lineHeight: '32px', color: '#bfbfbf' }}>至</Col>
                                            <Col span={11}>
                                                <Form.Item
                                                    name="sdkVersionEnd"
                                                    rules={[{ required: true, message: '请输入结束版本' }, { pattern: /^[1-9]\d*$/, message: 'SDK版本必须为正整数' }]}
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <Input placeholder="请输入结束版本号(正整数)" />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <div style={{ color: '#faad14', fontSize: 12, marginTop: 8 }}>满足此版本范围的设备才可订购该服务</div>
                                    </div>
                                )}
                            </div>

                            {/* 详情描述 */}
                            <div style={{ marginTop: 24 }}>
                                <SectionTitle>详情描述</SectionTitle>
                                <Form.Item
                                    label="套餐服务描述"
                                    name="desc"
                                    rules={[{ required: true, message: '请输入套餐服务描述' }]}
                                    extra={<span style={{ color: '#faad14', fontSize: 12 }}>注：此描述将展示给企业端查看，请务必准确填写服务内容。</span>}
                                >
                                    <Input.TextArea rows={4} placeholder="请输入对该套餐服务的详细描述..." maxLength={200} showCount />
                                </Form.Item>
                            </div>
                        </Form>
                    </div>
                </Drawer>
            </Layout>
        </ConfigProvider>
    );
};

export default PackageCombination;
